import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;
const MAX_COMPLETION_TEXT_LENGTH = 500;
const MAX_QUESTION_LENGTH = 300;
const MAX_OPTION_LENGTH = 100;
const MAX_QUESTIONS = 15;
const MAX_OPTIONS = 10;

const SurveyFormModal = ({ open, onClose, survey, clinicId, creatorId }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rewardPoints, setRewardPoints] = useState('');
  const [completionText, setCompletionText] = useState('Спасибо за прохождение опроса!');
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (survey) {
      setTitle(survey.title);
      setDescription(survey.description || '');
      setRewardPoints(survey.rewardPoints || '');
      setCompletionText(survey.completionText || 'Спасибо за прохождение опроса!');
      setQuestions(survey.questions || []);
    } else {
      resetForm();
    }
  }, [survey]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setRewardPoints('');
    setCompletionText('Спасибо за прохождение опроса!');
    setQuestions([]);
    setError('');
  };

  const validateForm = () => {
    if (!title.trim()) {
      setError('Введите название опроса');
      return false;
    }
    if (title.length > MAX_TITLE_LENGTH) {
      setError(`Название не должно превышать ${MAX_TITLE_LENGTH} символов`);
      return false;
    }
    if (description.length > MAX_DESCRIPTION_LENGTH) {
      setError(`Описание не должно превышать ${MAX_DESCRIPTION_LENGTH} символов`);
      return false;
    }
    if (rewardPoints && (isNaN(rewardPoints) || parseInt(rewardPoints) <= 0)) {
      setError('Количество баллов должно быть положительным числом');
      return false;
    }
    if (!completionText.trim()) {
      setError('Введите завершающий текст');
      return false;
    }
    if (completionText.length > MAX_COMPLETION_TEXT_LENGTH) {
      setError(`Завершающий текст не должен превышать ${MAX_COMPLETION_TEXT_LENGTH} символов`);
      return false;
    }
    if (questions.length === 0) {
      setError('Добавьте хотя бы один вопрос');
      return false;
    }
    if (questions.length > MAX_QUESTIONS) {
      setError(`Максимум ${MAX_QUESTIONS} вопросов`);
      return false;
    }
    for (const q of questions) {
      if (!q.question.trim()) {
        setError('Все вопросы должны быть заполнены');
        return false;
      }
      if (q.question.length > MAX_QUESTION_LENGTH) {
        setError(`Вопрос не должен превышать ${MAX_QUESTION_LENGTH} символов`);
        return false;
      }
      if (!q.options || q.options.length < 2) {
        setError('Каждый вопрос должен иметь минимум 2 варианта ответа');
        return false;
      }
      if (q.options.length > MAX_OPTIONS) {
        setError(`Максимум ${MAX_OPTIONS} вариантов ответа`);
        return false;
      }
      for (const opt of q.options) {
        if (!opt.trim()) {
          setError('Все варианты ответов должны быть заполнены');
          return false;
        }
        if (opt.length > MAX_OPTION_LENGTH) {
          setError(`Вариант ответа не должен превышать ${MAX_OPTION_LENGTH} символов`);
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = survey
        ? `${process.env.REACT_APP_API_URL}/web/surveys/${survey.id}`
        : `${process.env.REACT_APP_API_URL}/web/surveys`;

      const method = survey ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          clinicId,
          creatorId,
          title,
          description,
          rewardPoints: rewardPoints ? parseInt(rewardPoints) : null,
          completionText,
          questions,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при сохранении');
      }

      onClose(true);
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    if (questions.length >= MAX_QUESTIONS) {
      setError(`Максимум ${MAX_QUESTIONS} вопросов`);
      return;
    }
    setQuestions([
      ...questions,
      {
        question: '',
        type: 'single',
        options: ['', ''],
      },
    ]);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const addOption = (questionIndex) => {
    const updated = [...questions];
    if (updated[questionIndex].options.length >= MAX_OPTIONS) {
      setError(`Максимум ${MAX_OPTIONS} вариантов ответа`);
      return;
    }
    updated[questionIndex].options.push('');
    setQuestions(updated);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options = updated[questionIndex].options.filter(
      (_, i) => i !== optionIndex
    );
    setQuestions(updated);
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = value;
    setQuestions(updated);
  };

  const handleClose = () => {
    resetForm();
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {survey ? 'Редактировать опрос' : 'Создать новый опрос'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Название опроса (Видно только в админ.панели)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
          helperText={`${title.length}/${MAX_TITLE_LENGTH}`}
          inputProps={{ maxLength: MAX_TITLE_LENGTH }}
        />

        <TextField
          fullWidth
          label="Описание опроса"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          multiline
          rows={2}
          helperText={`${description.length}/${MAX_DESCRIPTION_LENGTH}`}
          inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
        />

        <TextField
          fullWidth
          label="Кол-во баллов за прохождение опроса (необязательно)"
          value={rewardPoints}
          onChange={(e) => setRewardPoints(e.target.value)}
          margin="normal"
          type="number"
          helperText="Количество баллов за прохождение опроса (оставьте поле пустым, если начисление не требуется)"
          inputProps={{ min: 1 }}
        />

        <TextField
          fullWidth
          label="Текст после прохождения опроса"
          value={completionText}
          onChange={(e) => setCompletionText(e.target.value)}
          margin="normal"
          multiline
          rows={2}
          required
          helperText={`Текст, который увидит пользователь после завершения опроса. ${completionText.length}/${MAX_COMPLETION_TEXT_LENGTH}`}
          inputProps={{ maxLength: MAX_COMPLETION_TEXT_LENGTH }}
        />

        <Box mt={3} mb={2}>
          <Typography variant="h6" gutterBottom>
            Вопросы
          </Typography>
        </Box>

        {questions.map((question, qIndex) => (
          <Paper key={qIndex} elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" mb={1}>
              <DragIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                Вопрос {qIndex + 1}
              </Typography>
              <IconButton
                color="error"
                size="small"
                onClick={() => removeQuestion(qIndex)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            <TextField
              fullWidth
              label="Текст вопроса"
              value={question.question}
              onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
              margin="normal"
              required
              helperText={`${question.question.length}/${MAX_QUESTION_LENGTH}`}
              inputProps={{ maxLength: MAX_QUESTION_LENGTH }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Тип ответа</InputLabel>
              <Select
                value={question.type}
                onChange={(e) => updateQuestion(qIndex, 'type', e.target.value)}
                label="Тип ответа"
              >
                <MenuItem value="single">Один вариант</MenuItem>
                <MenuItem value="multiple">Несколько вариантов</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" mt={2} mb={1}>
              Варианты ответов:
            </Typography>

            {question.options.map((option, oIndex) => (
              <Box key={oIndex} display="flex" alignItems="center" mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={option}
                  onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                  placeholder={`Вариант ${oIndex + 1}`}
                  inputProps={{ maxLength: MAX_OPTION_LENGTH }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeOption(qIndex, oIndex)}
                  disabled={question.options.length <= 2}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addOption(qIndex)}
              sx={{ mt: 1 }}
            >
              Добавить вариант
            </Button>
          </Paper>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addQuestion}
          fullWidth
        >
          Добавить вопрос
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Отмена
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyFormModal;
